/* HenaketScrollTop - the Henaket "Back to top" button.
 *
 * 0.42.54 - Fixed in the bottom-right, hidden until the citizen is both
 * past a share of the page AND scrolling up - that is, already heading
 * for the top. Clicking returns to the top.
 *
 * The threshold is the admin's to set; everything else is the spec's.
 */

import { useEffect, useRef, useState } from 'react';

import styles from './HenaketScrollTop.module.css';

/* The spec's arrow: 16x16, filled with the current text colour, after
 * the label. Its path is given verbatim and not re-drawn. */
const ARROW = 'M0 8L1.41 9.41L7 3.83V16H9V3.83L14.58 9.42L16 8L8 0L0 8Z';

export default function HenaketScrollTop({
  label = '',
  /* 0.42.56 - Past what share of the FIRST SCREEN it may appear: 100
   * means one full window height down, 150 a screen and a half.
   *
   * It used to be a share of the whole scrollable height, which meant
   * different things on different pages - 15% of a three-screen form is
   * half a screen, 15% of a twenty-screen report is three screens. A
   * window height is the same distance everywhere. */
  showFromPercent = 100,
  /* 0.42.56 - How long it stays after the citizen stops scrolling up,
   * in seconds. 0 keeps it for as long as they keep scrolling up.
   *
   * Without this, "while scrolling up" was nearly "always": one flick
   * upward left it on screen until the citizen scrolled down again. */
  visibleSeconds = 0,
  /* The spec shows it only while scrolling UP. Off means "any time past
   * the threshold", for a page where the way back should always be to
   * hand. */
  onlyWhenScrollingUp = true,
  /* Authoring surfaces draw it in the flow, always visible: an admin is
   * placing it, not scrolling a real page. */
  inline = false,
  /* 0.47.40 - The name a screen reader reads when there is no label
   * (the admin panel's icon-only button). */
  ariaLabel = '',
  /* 0.47.50 - 'warning': Henaket Warning light (the admin panel's,
   * to stand out over the editor's white and grey). Unset: Blue 100. */
  tone = '',
  /* 0.48.16 - 'up' (default): Back to top, shown while scrolling up.
     'down': To the bottom, its mirror, shown past one screen from the
     end while scrolling down. */
  direction = 'up',
  /* 0.48.16 - At the mouse's height at the right edge (the admin
     panel), so it is at hand wherever the admin is looking. Without a
     mouse (touch) it stays at the bottom right. */
  followPointer = false,
}) {
  const down = direction === 'down';
  const [visible, setVisible] = useState(false);
  const [pointerY, setPointerY] = useState(null);
  /* 0.48.19 - The mouse's latest height, kept without redrawing; the
     button takes it only when it appears (when the scroll starts), and
     then stays there until it hides. */
  const mouseY = useRef(null);
  const shownRef = useRef(false);
  const show = (on) => {
    if (on && !shownRef.current && followPointer && mouseY.current != null) setPointerY(mouseY.current);
    shownRef.current = on;
    setVisible(on);
  };
  const lastY = useRef(0);
  // 0.42.56 - The pending hide, so a new upward movement can cancel it.
  const hideTimer = useRef(null);

  useEffect(() => {
    if (inline) return undefined;
    lastY.current = window.scrollY;
    const share = Math.max(0, Number(showFromPercent) || 0) / 100;
    const holdMs = Math.max(0, Number(visibleSeconds) || 0) * 1000;

    const onScroll = () => {
      const y = window.scrollY;
      /* 0.42.56 - Measured against the FIRST SCREEN, not the page: the
       * same distance on a short form and a long report.
       *
       * A page that cannot scroll at all still shows nothing, because
       * there y never moves from 0. */
      const toEnd = document.documentElement.scrollHeight - window.innerHeight - y;
      const farEnough = down ? toEnd > window.innerHeight * share : y > window.innerHeight * share;
      // 0.48.16 - "Up" is the way the button goes: down for To the bottom.
      const scrollingUp = down ? y > lastY.current : y < lastY.current;
      lastY.current = y;

      if (!farEnough) { clear(); show(false); return; }
      if (!onlyWhenScrollingUp) { show(true); return; }
      if (scrollingUp) {
        show(true);
        /* Each upward movement starts the wait again, so it stays while
         * the citizen keeps going up and fades once they settle. */
        if (holdMs > 0) {
          clear();
          hideTimer.current = window.setTimeout(() => show(false), holdMs);
        }
      } else {
        // Going down: gone at once, as the design specifies.
        clear();
        show(false);
      }
    };

    const clear = () => {
      if (hideTimer.current) {
        window.clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clear();
    };
  }, [inline, showFromPercent, onlyWhenScrollingUp, visibleSeconds, down]);

  /* 0.48.16 - At the mouse's height. 0.48.19 - Only where the mouse was
     when the scroll started (see show): moving the mouse afterwards
     does not move the button. */
  useEffect(() => {
    if (inline || !followPointer) return undefined;
    const onMove = (ev) => {
      if (ev.pointerType && ev.pointerType !== 'mouse') return;
      mouseY.current = ev.clientY;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [inline, followPointer]);
  const pointerStyle = followPointer && !inline && pointerY != null
    ? { top: Math.max(8, Math.min(pointerY - 20, window.innerHeight - 48)), bottom: 'auto' }
    : undefined;

  const shown = inline || visible;

  const toTop = () => {
    /* Smooth, unless the viewer has asked for less motion - a long
     * animated scroll is exactly what that setting is about. */
    let smooth = true;
    try {
      smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { /* no matchMedia: keep the default */ }
    window.scrollTo({ top: down ? document.documentElement.scrollHeight : 0, behavior: smooth ? 'smooth' : 'auto' });
  };

  return (
    <button
      type="button"
      className={`${styles.button} ${shown ? styles.visible : ''} ${inline ? styles.inline : ''} ${label ? '' : styles.iconOnly} ${tone === 'warning' ? styles.warning : ''}`}
      aria-label={label ? undefined : (ariaLabel || (down ? 'To the bottom' : 'Back to top'))}
      title={label ? undefined : (ariaLabel || (down ? 'To the bottom' : 'Back to top'))}
      style={pointerStyle}
      /* Hidden means hidden: out of the tab order and unseen by a screen
       * reader, so nobody can reach a control that is not there. */
      aria-hidden={shown ? undefined : 'true'}
      tabIndex={shown ? 0 : -1}
      onClick={inline ? undefined : toTop}
    >
      {label}
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"
           style={down ? { transform: 'rotate(180deg)' } : undefined}>
        <path d={ARROW} />
      </svg>
    </button>
  );
}
